/** Save an in-memory blob as a file download. */
export function saveBlob(blob: Blob, filename: string): void {
  const blobUrl = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = blobUrl;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(blobUrl);
}

/**
 * Force-download a file from a URL. Resolves true only once the file was
 * handed to the browser; never navigates or opens a window, so it is safe
 * outside a user gesture (effects, pollers).
 *
 * The `download` attribute on `<a>` only works for same-origin URLs.
 * For cross-origin S3 presigned URLs we fetch the blob first, create
 * a local object URL, then trigger the download from that.
 */
export async function downloadFile(
  url: string,
  filename?: string,
): Promise<boolean> {
  try {
    const res = await fetch(url);
    if (!res.ok) return false;
    saveBlob(await res.blob(), filename ?? inferFilename(url));
    return true;
  } catch {
    return false;
  }
}

/** Open the URL in a new tab. Call it straight from a click handler. */
export function openDownloadUrl(url: string): void {
  window.open(url, "_blank", "noopener,noreferrer");
}

/** For click handlers: fall back to a new tab when the blob fetch fails. */
export async function downloadFileOrOpen(
  url: string,
  filename?: string,
): Promise<void> {
  if (!(await downloadFile(url, filename))) openDownloadUrl(url);
}

function inferFilename(url: string): string {
  try {
    const path = new URL(url).pathname;
    const last = path.split("/").pop();
    return last || "download";
  } catch {
    return "download";
  }
}
